// Adopted from Svelte UX under the MIT License: https://www.github.com/techniq/svelte-ux

import { browser } from "./env";

/** @type {import("./defs").LogLevels} */
const logLevels = Object.freeze({
	trace: 'TRACE',
	debug: 'DEBUG',
	info: 'INFO',
	warn: 'WARN',
	error: 'ERROR'
});

/**
 * @param {string} value 
 * @returns {import("./defs").LogLevel?}
 */
export function toLogLevel(value) {
	if (!value) return null;
	const uc = value.toUpperCase();
	if (!Object.keys(logLevels).includes(uc)) return null;
	return /** @type {import("./defs").LogLevel} */ (uc);
}

/** @type {import("./defs").LogCategoryMap} */
const categoryMap = new Map();

/** @type {boolean} If true, every log message will directly log to the console with debug information about the log message itself. */
const hyperlog = false;

export class LoggerOptions {
	/** @type {import("./defs").LogLevel} */
	#defaultLevel = logLevels.info;
	/** @type {import("./defs").LogCategoryMap} */
	#categoryMap = categoryMap;

  /** @type {boolean} Flag to ensure that it has loaded at least once since the page was loaded. */
  #loaded = false;

  ensureLoaded() {
    if (this.#loaded) return;
    this.load();
    this.#loaded = true;
  }

	/**
	 * A readonly object storing available log levels.
	 * @returns {import("./defs").LogLevels}
	 */
	get levels() {
		return logLevels;
	}

	/**
	 * The default LogLevel attributed to log categories without one. Changing this property implicitly saves the options.
	 * @returns {import("./defs").LogLevel}
	 */
	get defaultLevel() {
		return this.#defaultLevel;
	}

	/**
	 * The default LogLevel attributed to log categories without one. Changing this property implicitly saves the options.
	 * @param {import("./defs").LogLevel} value
	 */
	set defaultLevel(value) {
		const s = this.#defaultLevel !== value;
		this.#defaultLevel = value;
		if (s) {
			this.save();
		}
	}

	/**
	 * Resets the category map to match localStorage to facilitate editing. Note that 
	 * @returns {void}
	 */
	load() {
		if (!browser) return;
		/** @type {string} */
		const storedLoggers = (window.localStorage.logger ?? "");
		const categories = storedLoggers.split(",").filter(v => !!v).map(pair => pair.split(":"));
		this.#categoryMap.clear();
		for (let [name, level] of categories) {
			const lv = toLogLevel(level);
			this.#categoryMap.set(name, lv);
		}
	}

	save() {
		if (!browser) return;
    this.ensureLoaded();
		let v = "";
		for (let [name, level] of this.#categoryMap.entries()) {
			if (v.length > 0) {
				v += ",";
			}
			v += (level === this.defaultLevel || !level) ? name : `${name}:${level}`;
		}
		window.localStorage.logger = v;
	}
}

const loggerOptions = new LoggerOptions();

/**
 * Logs to the console with color-coded messages and a prefixed category name.
 * The active loggers can be centrally configured via localStorage.
 * 
 * @example
 * Enable (explicit):
 *   localStorage.logger = 'SelectField';
 *   localStorage.logger = 'SelectField:INFO';
 *   localStorage.logger = 'SelectField,Dialog';
 *   localStorage.logger = 'SelectField:INFO,Dialog';
 * 
 * Enable (programmatic):
 *   Logger.addLogger("MyLoggerCategory");
 * 
 * Use:
 *   import { Logger } from "$lib/core/js/util/logger";
 *   const logger = new Logger("MyClass");
 *   logger.trace('...');
 *   logger.debug('...');
 *   logger.info('...');
 *   logger.warn('...');
 *   logger.error('...');
 */
export class Logger {
	/** @type {LoggerOptions} */
	static options = loggerOptions;

	/** @type {string} */
  name;

	/**
	 * @param {string} name 
	 */
  constructor(name) {
    this.name = name;
  }

	/**
	 * Provides a Map<string, LogLevel?> of the logger configuration in a callback for easy editing. Then saves (overwrites) `window.localStorage.logger` to persist the changes.
	 * @param {(map: import("./defs").LogCategoryMap) => void} callback The callback in which to mutate the log category map.
	 * @param {boolean} [load=false] If true, loads the map from localStorage before `callback`. Use to avoid losing data when localStorage is externally modified. Defaults to `false`.
	 * @example
	 * Logger.updateLoggers(m => m.set("App", "WARN")
	 *   .set("MyComponent", "INFO"));                                 // "App,MyComponent:INFO"
	 * window.localStorage.logger += ",Data:ERROR";                    // "App,MyComponent:INFO,Data:ERROR"
	 * Logger.updateLoggers(m => m.set("MyComponent", "DEBUG"), true); // "App,MyComponent,Data:ERROR"
	 * window.localStorage.logger = "App";                             // "App"
	 * Logger.updateLoggers(m => m.remove("Data"), false);             // "App,MyComponent"
	 */
	static updateLoggers(callback, load = false) {
		if (hyperlog) {
			console.log("Updating loggers:", { callback, load });
		}
    this.options.ensureLoaded();
		if (load) {
			this.options.load();
		}
		callback(categoryMap);
		this.options.save();
	}

  /**
   * Efficiently adds a logger with the default level while avoiding duplicates.
   * @param {string} name
   */
  static addLogger(name) {
    this.options.ensureLoaded();
		if (!categoryMap.has(name)) {
			categoryMap.set(name, null);
		}
    Logger.options.save();
  }

	/**
	 * Find registered data associated with Logger and return as an array containing the logger name, then level.
	 * 
	 * @param {string} logger 
	 * @returns {[string,import("./defs").LogLevel?]|undefined}
	 */
	static findLogger(logger) {
    const enabledLoggers = browser
      ? (localStorage
          .getItem('logger')
          ?.split(',')
          .map((x) => /** @type {[string, import("./defs.js").LogLevel?]} */ (x.split(':'))) ?? [])
      : [];

    const enabledLogger = enabledLoggers.find((x) => x[0] === logger);

		return enabledLogger;
	}

	/**
	 * Gets the raw log level associated with the given `logger`.
	 * @param {string} logger 
	 * @returns {import("./defs").LogLevel?}
	 */
	static getLevel(logger) {
		const enabledLogger = this.findLogger(logger);

		return enabledLogger == null || enabledLogger.length > 1 ? null : /** @type {import("./defs").LogLevel} */ (enabledLogger[1]);
	}

	/**
	 * @param  {...any} message 
	 */
  trace(...message) {
    this.log('TRACE', ...message);
  }

	/**
	 * @param  {...any} message 
	 */
  debug(...message) {
    this.log('DEBUG', ...message);
  }

	/**
	 * @param  {...any} message 
	 */
  info(...message) {
    this.log('INFO', ...message);
  }

	/**
	 * @param  {...any} message 
	 */
  warn(...message) {
    this.log('WARN', ...message);
  }

	/**
	 * @param  {...any} message 
	 */
  error(...message) {
    this.log('ERROR', ...message);
  }

	/**
	 * 
	 * @param {import("./defs.js").LogLevel} level 
	 * @param  {...any} message 
	 */
  log(level, ...message) {
    const enabledLogger = Logger.findLogger(this.name);

		const levelList = [...Object.values(Logger.options.levels)];

		let shouldLog = false;
		if (hyperlog) {
			const target = levelList.indexOf(level);
			const actual = enabledLogger == null ? undefined : enabledLogger[1];
			const simulated = enabledLogger == null ? Logger.options.defaultLevel : enabledLogger[1] ?? Logger.options.defaultLevel;
			const value = levelList.indexOf(simulated);
			shouldLog = enabledLogger != null && target >= value;

			console.log({
				level,
				name: this.name,
				actual,
				simulated,
				target,
				value,
				message,
				shouldLog,
			});
		} else {
			shouldLog =
				enabledLogger != null &&
				levelList.indexOf(level) >= levelList.indexOf(enabledLogger[1] ?? Logger.options.defaultLevel);
		}

    if (shouldLog) {
      switch (level) {
        case 'TRACE':
          console.trace(
            `%c${this.name} %c${level}`,
            'color: hsl(200deg, 10%, 50%)',
            'color: hsl(200deg, 40%, 50%)',
            ...message
          );
          break;

        case 'DEBUG':
          console.log(
            `%c${this.name} %c${level}`,
            'color: hsl(200deg, 10%, 50%)',
            'color: hsl(200deg, 40%, 50%)',
            ...message
          );
          break;

        case 'INFO':
          console.log(
            `%c${this.name} %c${level}`,
            'color: hsl(200deg, 10%, 50%)',
            'color: hsl(60deg, 100%, 50%)',
            ...message
          );
          break;

        case 'WARN':
          console.warn(
            `%c${this.name} %c${level}`,
            'color: hsl(200deg, 10%, 50%)',
            'color: hsl(30deg, 100%, 50%)',
            ...message
          );
          break;

        case 'ERROR':
          console.warn(
            `%c${this.name} %c${level}`,
            'color: hsl(200deg, 10%, 50%)',
            'color: hsl(0deg, 100%, 50%)',
            ...message
          );
          break;
      }
    }
  }
}